import supabase from '../supabaseClient';
import {
  FEE_TYPES,
  getBillingRateDate,
  getBillingServiceType,
  getFeeTypeForBillingService,
  getFeeTypeForSession,
  isSundayDate,
} from './serviceTypeUtils';

const IE_SERVICE_FEE = 2200;

export { IE_SERVICE_FEE };

export async function getValidServiceFee(therapistId, date, feeType = FEE_TYPES.REGULAR) {
  try {
    const { data, error } = await supabase
      .from('service_fees')
      .select('amount')
      .eq('therapist_id', therapistId)
      .eq('fee_type', feeType)
      .lte('valid_from', date)
      .or(`valid_until.is.null,valid_until.gte.${date}`)
      .order('valid_from', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (error) throw error;
    return data?.amount || 0;
  } catch (error) {
    console.error('Error getting service fee:', error);
    return 0;
  }
}

export function getServiceFeeFromMap(feeMap, therapistId, date, feeType = FEE_TYPES.REGULAR) {
  const fees = feeMap.get(therapistId) || [];

  const validFees = fees.filter((fee) => {
    const matchesType = (fee.fee_type || FEE_TYPES.REGULAR) === feeType;
    const validFrom = fee.valid_from <= date;
    const validUntil = !fee.valid_until || fee.valid_until >= date;
    return matchesType && validFrom && validUntil;
  });

  if (validFees.length === 0) {
    return 0;
  }

  const mostRecentFee = validFees.reduce((latest, current) =>
    current.valid_from > latest.valid_from ? current : latest
  );

  return mostRecentFee.amount || 0;
}

export function resolveSessionFeeType({
  billingServiceType,
  date,
  feeMap,
  therapistId,
}) {
  const primaryFeeType = getFeeTypeForSession(billingServiceType, date);

  if (!isSundayDate(date) || !feeMap) {
    return primaryFeeType;
  }

  const sundayAmount = getServiceFeeFromMap(feeMap, therapistId, date, primaryFeeType);
  if (sundayAmount > 0) {
    return primaryFeeType;
  }

  return getFeeTypeForBillingService(billingServiceType);
}

export function getSessionServiceFee({
  session,
  therapistSpecialization,
  feeMap,
  therapistId,
  date,
}) {
  if (session.session_type === 'IE') {
    return IE_SERVICE_FEE;
  }

  const billingServiceType = getBillingServiceType(session, therapistSpecialization);
  const billingRateDate = getBillingRateDate(date, session);
  const feeType = resolveSessionFeeType({
    billingServiceType,
    date: billingRateDate,
    feeMap,
    therapistId,
  });

  if (feeMap) {
    const amount = getServiceFeeFromMap(feeMap, therapistId, date, feeType);
    if (amount > 0) {
      return amount;
    }

    if (
      isSundayDate(billingRateDate) &&
      feeType !== getFeeTypeForBillingService(billingServiceType)
    ) {
      return getServiceFeeFromMap(
        feeMap,
        therapistId,
        date,
        getFeeTypeForBillingService(billingServiceType)
      );
    }

    return amount;
  }

  return null;
}

export async function getSessionServiceFeeAsync({
  session,
  therapistSpecialization,
  therapistId,
  date,
}) {
  if (session.session_type === 'IE') {
    return IE_SERVICE_FEE;
  }

  const billingServiceType = getBillingServiceType(session, therapistSpecialization);
  const billingRateDate = getBillingRateDate(date, session);
  const feeType = getFeeTypeForSession(billingServiceType, billingRateDate);
  const primaryAmount = await getValidServiceFee(therapistId, date, feeType);

  if (primaryAmount > 0) {
    return primaryAmount;
  }

  if (
    isSundayDate(billingRateDate) &&
    feeType !== getFeeTypeForBillingService(billingServiceType)
  ) {
    return getValidServiceFee(
      therapistId,
      date,
      getFeeTypeForBillingService(billingServiceType)
    );
  }

  return primaryAmount;
}
